import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, CopyIcon, Maximize2Icon, XIcon } from 'lucide-react';

/**
 * Copying, with a path for browsers that will not give us the async clipboard.
 *
 * `navigator.clipboard` needs a secure context, so it is simply absent over
 * plain http and inside some embedded webviews. Since the entire point of this
 * component is the copy button, the old selection-based route is worth keeping
 * rather than leaving the reader to select ten kilobytes of text by hand.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through — a rejected permission is not a reason to give up.
  }
  try {
    const holder = document.createElement('textarea');
    holder.value = text;
    // Off-screen rather than hidden: `display: none` cannot be selected.
    holder.style.position = 'fixed';
    holder.style.top = '-9999px';
    holder.setAttribute('readonly', '');
    document.body.appendChild(holder);
    holder.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(holder);
    return ok;
  } catch {
    return false;
  }
}
function CopyButton({
  text,
  subtle
}: {
  text: string;
  subtle?: boolean;
}) {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');

  // Reset after a beat, and cancel on unmount so closing the dialog mid-timer
  // does not set state on something that is gone.
  useEffect(() => {
    if (state === 'idle') return;
    const timer = window.setTimeout(() => setState('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [state]);
  const label = state === 'done' ? 'Copied' : state === 'failed' ? 'Press ⌘C' : 'Copy';
  return <button type="button" onClick={async () => setState((await copyText(text)) ? 'done' : 'failed')} aria-label={`Copy the prompt${state === 'done' ? ' — copied' : ''}`} className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] transition-colors duration-150 ease-expo ${subtle ? 'text-ink-faint hover:text-ink' : 'border border-rule text-ink-muted hover:border-ink/20 hover:text-ink'}`} data-id="element-45" data-mp="35hl1czwg5fuz">
      {state === 'done' ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-46" data-mp="2cmmymiktfxdq" /> : <CopyIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-47" data-mp="27ocz5vs26amb" />}
      {label}
    </button>;
}

/**
 * A long prompt, shown as an artifact rather than as body copy.
 *
 * It is about seventeen hundred words, which is far too much to drop inline and
 * exactly the right amount for the job it does — so the reader gets the opening
 * as evidence that it is real and specific, one button to take it, and one to
 * read the whole thing. Nobody has to scroll past it to continue the article.
 *
 * The preview is a plain height clip rather than a line count, because these
 * lines wrap differently at every width and slicing by newline gives a box that
 * changes height as the window changes.
 */
export function PromptCard({
  text,
  label = 'Prompt',
  note
}: {
  text: string;
  label?: string;
  note?: string;
}) {
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const words = text.trim().split(/\s+/).length;
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    // Without this the page behind scrolls when the dialog's own content runs
    // out, which is disorienting on a piece this long.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
      // Send focus back where it came from, or it lands at the top of the page.
      opener.current?.focus();
    };
  }, [open]);
  return <>
      <div className="border border-rule bg-surface" data-id="element-48" data-mp="zi3k4xgwx5vh">
        <div className="flex items-center gap-3 border-b border-rule px-4 py-2.5" data-id="element-49" data-mp="1ee2qa2kgovxz">
          <span className="text-[13px] text-ink" data-id="element-50" data-mp="1ebpy6wkzp577">{label}</span>
          <span className="text-[12px] tabular-nums text-ink-faint" data-id="element-51" data-mp="19e6lw27ur2hw">
            {words.toLocaleString()} words
          </span>
          <span className="ml-auto flex items-center gap-1" data-id="element-52" data-mp="1nco27evfhcps">
            <CopyButton text={text} subtle data-id="element-53" data-mp="1iemiqsu3bbds" />
          </span>
        </div>

        <div className="relative" data-id="element-54" data-mp="1x53kkdan03b7">
          <pre className="max-h-[15rem] overflow-hidden whitespace-pre-wrap px-4 py-4 font-mono text-[12.5px] leading-relaxed text-ink-muted" data-id="element-55" data-mp="1x40w0c0secyg">
            {text}
          </pre>
          {/* Fades the clip rather than cutting it mid-letter, which reads as a
              rendering fault rather than as “there is more”. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-surface" aria-hidden="true" data-id="element-56" data-mp="221kc7hh1ozt1" />
        </div>

        <div className="border-t border-rule px-4 py-2.5" data-id="element-57" data-mp="2poxxj06xmhmv">
          <button ref={opener} type="button" onClick={() => setOpen(true)} className="group inline-flex items-center gap-1.5 text-[13px] text-ink-muted transition-colors duration-150 ease-expo hover:text-ink" data-id="element-58" data-mp="2bq7pwqmhkzk9">
            <Maximize2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-59" data-mp="26rwggh03sr2k" />
            Read the full prompt
          </button>
        </div>
      </div>

      {note && <p className="mt-3 text-[13px] leading-relaxed text-ink-faint" data-id="element-60" data-mp="1t2mabhm2oz7x">{note}</p>}

      {open && <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-ink/20 p-0 backdrop-blur-sm sm:items-center sm:p-6" onMouseDown={event => {
      // Only a click that both starts and ends on the backdrop closes it,
      // so a drag-select that runs off the edge of the text does not.
      if (event.target === event.currentTarget) setOpen(false);
    }} data-id="element-61" data-mp="1x2mnz01cl6dx">
          <div ref={panel} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} className="flex max-h-full w-full max-w-[52rem] flex-col bg-paper outline-none sm:border sm:border-rule" data-id="element-62" data-mp="3gyk11fvblb92">
            <div className="flex items-center gap-3 border-b border-rule px-5 py-3" data-id="element-63" data-mp="ci80ioe1kfbf">
              <span className="text-[14px] text-ink" data-id="element-64" data-mp="8jifc9mrhth8">{label}</span>
              <span className="text-[12px] tabular-nums text-ink-faint" data-id="element-65" data-mp="3ikzi3p85biw">
                {words.toLocaleString()} words
              </span>
              <span className="ml-auto flex items-center gap-1" data-id="element-66" data-mp="3us1ld2vtys05">
                <CopyButton text={text} data-id="element-67" data-mp="2oao6oht83vfg" />
                <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-md p-1.5 text-ink-faint transition-colors duration-150 ease-expo hover:text-ink" data-id="element-68" data-mp="2o7ihz3oiavk9">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-69" data-mp="2e67t5v37uqet" />
                </button>
              </span>
            </div>

            <pre className="overflow-auto whitespace-pre-wrap px-5 py-5 font-mono text-[13px] leading-relaxed text-ink-muted" data-id="element-70" data-mp="2nvrauxf70e83">
              {text}
            </pre>
          </div>
        </div>}
    </>;
}